import { useState, useEffect, useContext } from "react"
import { useSearchParams } from "react-router-dom"
import WebinarList from "../components/webinar/WebinarList"
import SearchBar from "../components/SearchBar"
import { useWebinars } from "../hooks/useWebinars"
import { Button } from "../components/ui/button"
import { useTranslation } from "../i18n"
import { TranslationContext } from "../i18n/TranslationContext"
import { useAuth } from "../hooks/useAuth"

export default function WebinarsPage() {
  const { tObj } = useTranslation()
  const translationContext = useContext(TranslationContext)
  const userLanguage = translationContext?.language || 'fr'
  const { isAuthenticated } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState("ALL")
  const [page, setPage] = useState(1)
  const [webinars, setWebinars] = useState([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [registeringWebinarId, setRegisteringWebinarId] = useState(null)
  const [cancellingWebinarId, setCancellingWebinarId] = useState(null)
  
  const { getWebinars, registerToWebinar, cancelRegistration, loading } = useWebinars()

  // Charger les webinaires (inclut le terme de recherche si présent)
  const loadWebinars = async () => {
    try {
      const params = {
        status: statusFilter,
        page,
        limit: 12,
        language: userLanguage
      }
      if (searchQuery && searchQuery.trim()) {
        params.search = searchQuery.trim()
      }
      const data = await getWebinars(params)
      setWebinars(data?.data || [])
      setTotal(data?.total || 0)
      setTotalPages(data?.totalPages || 1)
    } catch (error) {
      console.error("Erreur lors du chargement des webinaires:", error)
    }
  }

  useEffect(() => {
    loadWebinars()
  }, [statusFilter, page, userLanguage, searchQuery])

  // Lire les paramètres d'URL au chargement
  useEffect(() => {
    const urlStatus = searchParams.get('status')
    const urlPage = searchParams.get('page')
    const urlSearch = searchParams.get('search')
    
    if (urlStatus) {
      setStatusFilter(urlStatus)
    }
    if (urlPage) {
      setPage(parseInt(urlPage, 10))
    }
    if (urlSearch) {
      setSearchQuery(urlSearch)
    }
  }, [searchParams])

  const handleStatusChange = (newStatus) => {
    setStatusFilter(newStatus)
    setPage(1)
    setSearchParams({ status: newStatus, page: '1' })
  }

  // Handler partagé pour la SearchBar (synchronise URL et lance la recherche)
  // Gère les deux formats : string (variant="hero") et objet { term, filters } (variant="default")
  const handleSearch = (input) => {
    // Extraire le terme selon le format
    const term = typeof input === 'string' ? input : (input?.term || "")
    const normalized = term?.trim?.() || ""
    setSearchQuery(normalized)
    setPage(1)
    // Mettre à jour les params d'URL (conserver le filtre status si présent)
    const params = {}
    if (statusFilter) params.status = statusFilter
    if (normalized) params.search = normalized
    params.page = "1"
    setSearchParams(params)
  }

  const handleRegister = async (webinarId) => {
    setRegisteringWebinarId(webinarId)
    try {
      await registerToWebinar(webinarId)
      loadWebinars() // Recharger pour mettre à jour isRegistered
    } catch (error) {
      // L'erreur est déjà gérée dans le hook
    } finally {
      setRegisteringWebinarId(null)
    }
  }

  const handleCancelRegistration = async (webinarId) => {
    setCancellingWebinarId(webinarId)
    try {
      await cancelRegistration(webinarId)
      loadWebinars() // Recharger pour mettre à jour isRegistered
    } catch (error) {
      // L'erreur est déjà gérée dans le hook
    } finally {
      setCancellingWebinarId(null)
    }
  }

  // Filtre côté client (complémentaire au filtrage côté API) - insensible à la casse
  const filteredWebinars = webinars.filter(webinar => {
    if (!searchQuery.trim()) return true
    const query = searchQuery.toLowerCase()
    return (
      (webinar.title || "").toLowerCase().includes(query) ||
      (webinar.description || "").toLowerCase().includes(query)
    )
  })

  return (
    <div className="min-h-screen bg-[#FAFBFD]">
      {/* Header */}
      <div className="pt-20 pb-12 sm:pt-24 sm:pb-16 lg:pt-28 lg:pb-20 border-b border-[#D5DAE3]">
        <div className="container mx-auto px-3 sm:px-4 md:px-6">
          {/* Header */}
          <div className="mb-10 text-center">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#203a8a] mb-4">
              {tObj.webinars?.title?.() || "Webinaires"}
            </h1>
            <p className="font-sans text-lg sm:text-xl text-[#6B7280] max-w-2xl mx-auto leading-relaxed">
              {tObj.webinars?.subtitle?.() || "Découvrez nos webinaires et inscrivez-vous"}
            </p>
          </div>

          {/* Barre de recherche */}
          <div className="mt-10 flex justify-center">
            <div className="w-full max-w-2xl">
              <SearchBar
                placeholder={tObj.webinars?.searchPlaceholder?.() || "Rechercher un webinaire..."}
                onSearch={handleSearch}
                defaultValue={searchQuery}
                variant="hero"
                showSearchButton={true}
              />
            </div>
          </div>
        </div>
      </div>

        {/* Filtres de statut */}
        <div className="container mx-auto px-3 sm:px-4 md:px-6">
          <div className="py-6 sm:py-8 bg-white border-b border-[#D5DAE3] mb-6">
            <div className="flex flex-wrap gap-2 sm:gap-3 justify-center">
              {[
                { key: "ALL", label: tObj.webinars?.filters?.all?.() || "Tous" },
                { key: "UPCOMING", label: tObj.webinars?.filters?.upcoming?.() || "À venir" },
                { key: "ONGOING", label: tObj.webinars?.filters?.ongoing?.() || "En cours" },
                { key: "COMPLETED", label: tObj.webinars?.filters?.completed?.() || "Terminés" },
              ].map((filter) => (
                <Button
                  key={filter.key}
                  variant={statusFilter === filter.key ? "default" : "outline"}
                  onClick={() => handleStatusChange(filter.key)}
                  className={`text-xs sm:text-sm px-4 py-2 text-xs uppercase tracking-[0.22em] font-semibold ${
                    statusFilter === filter.key
                      ? "bg-[#203a8a] text-white hover:bg-[#1a2f6e]"
                      : "border border-[#D5DAE3] text-[#6B7280] hover:border-[#203a8a] hover:text-[#203a8a] bg-white"
                  }`}
                >
                  {filter.label}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Message si non authentifié */}
        {!isAuthenticated && (
          <div className="container mx-auto px-3 sm:px-4 md:px-6 mb-6">
            <div className="p-4 bg-[#FAFBFD] border border-[#D5DAE3]">
              <p className="font-sans text-sm text-[#203a8a] font-semibold">
                {tObj.webinars?.loginPrompt?.() || "Connectez-vous pour vous inscrire aux webinaires"}
              </p>
            </div>
          </div>
        )}

        {/* Liste des webinaires */}
        <div className="container mx-auto px-3 sm:px-4 md:px-6 pb-6 sm:pb-8">
        {loading && webinars.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">{tObj.common?.loading?.() || "Chargement..."}</p>
          </div>
        ) : filteredWebinars.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">
              {searchQuery 
                ? tObj.webinars?.noWebinars?.() || "Aucun webinaire trouvé"
                : tObj.webinars?.noWebinars?.() || "Aucun webinaire disponible"}
            </p>
          </div>
        ) : (
          <>
            <WebinarList
              webinars={filteredWebinars}
              loading={loading}
              onRegister={handleRegister}
              onCancelRegistration={handleCancelRegistration}
              registeringWebinarId={registeringWebinarId}
              cancellingWebinarId={cancellingWebinarId}
            />

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 mb-6 flex justify-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    const newPage = Math.max(1, page - 1)
                    setPage(newPage)
                    const params = { status: statusFilter, page: newPage.toString() }
                    if (searchQuery) params.search = searchQuery
                    setSearchParams(params)
                  }}
                  disabled={page === 1}
                  className="text-xs uppercase tracking-[0.22em] border border-[#D5DAE3] text-[#6B7280] hover:border-[#203a8a] hover:text-[#203a8a]"
                >
                  {tObj.common?.previous?.() || "Précédent"}
                </Button>
                <span className="flex items-center px-4 font-sans text-sm text-[#6B7280]">
                  {tObj.webinars?.pagination?.() || "Page"} {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => {
                    const newPage = Math.min(totalPages, page + 1)
                    setPage(newPage)
                    const params = { status: statusFilter, page: newPage.toString() }
                    if (searchQuery) params.search = searchQuery
                    setSearchParams(params)
                  }}
                  disabled={page === totalPages}
                  className="text-xs uppercase tracking-[0.22em] border border-[#D5DAE3] text-[#6B7280] hover:border-[#203a8a] hover:text-[#203a8a]"
                >
                  {tObj.common?.next?.() || "Suivant"}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

